import { HashedSignupData } from '../../Views/Blocks/SignupBlock/classes/HashedSignupData';
import { GoogleEnhancedConversionsDataLayerEvent as DataLayerEvent } from './GoogleEnhancedConversionsDataLayerEvent';
import { SignupPayload } from '../../Views/Blocks/SignupBlock/classes/SignupPayload';
import { sha256Hash } from '../utilities/SHA256Hasher';
import {
  normaliseContactTelephone,
  normaliseEmail,
} from '../../Views/Blocks/SignupBlock/utils/SignupDataNormaliser';
import { MarketingAutomationSubmissionType, Tracker } from './tracker';

const SUCCESSFUL_SIGNUP_EVENT_NAME =
  'gtm-sign-up-hero-block-marketing__success' satisfies string;
const SUCCESSFUL_SIGNUP_EVENT_OUTPUT = 'Submitted form' satisfies string;

type DataLayerPushContext = {
  signupData: SignupPayload;
  teamSize: number;
  dataLayer: DataLayerEvent[];
};

declare global {
  interface Window {
    dataLayer: Array<DataLayerEvent>;
  }
}

export class DataLayerPusherSignupTracker implements Tracker {
  fireSignupSuccess(
    signupData: SignupPayload,
    teamSize: number,
  ): Promise<void> {
    window.dataLayer = window.dataLayer || [];

    const context: DataLayerPushContext = {
      signupData,
      teamSize,
      dataLayer: window.dataLayer,
    };

    return this.pushSignupSuccessData(context);
  }

  fireSignupStep(step: number): Promise<void> {
    // No-op - this tracker is not interested in tracking the individual signup steps
    // it should only be used for tracking the signup success event.
    // This may change in the future, ergo the interface is still implemented
    return Promise.resolve();
  }

  fireMarketingAutomationSubmission(
    type: MarketingAutomationSubmissionType,
  ): Promise<void> {
    // No-op - as the marketing automation forms do not guarantee the existence of hashable user data (email, phone, etc)
    // This may change in the future, ergo the interface is still implemented
    return Promise.resolve();
  }

  private async pushSignupSuccessData({
    signupData,
    teamSize,
    dataLayer,
  }: DataLayerPushContext): Promise<void> {
    // do not do this in the hash function as different types might have different normalisations applied
    const normalisedEmail = normaliseEmail(signupData.email);
    const normalisedContact = normaliseContactTelephone(
      signupData.contactTelephone,
    );

    dataLayer.push(
      new DataLayerEvent({
        enhancedConversionUserData: new HashedSignupData(
          await sha256Hash(normalisedEmail),
          await sha256Hash(normalisedContact),
        ),
        event: SUCCESSFUL_SIGNUP_EVENT_NAME,
        output: SUCCESSFUL_SIGNUP_EVENT_OUTPUT,
        business_size: teamSize,
      }),
    );
  }
}
